import {
	OrderItemLifecycleStepEnum as Step,
	OrderPaymentOptionScope,
	SampleActionActor,
	type GetClientOrderQuery,
} from '@codezo/graphql';
import { Thumb } from '@codezo/quote-detail';
import { ActionBox, ActionContext } from '@codezo/order-detail';
import { Button } from '../../../components/ui/button';
import { CurrencyText } from '../../../components/CurrencyText';
import { ClientSampleRounds, liveSample } from './ClientSampleRounds';
import { ClientItemShipments } from './ClientItemShipments';
import {
	FlowDisclosure,
	FlowSubSteps,
	OrderFlow,
	OwnerChip,
	type FlowRow,
	type FlowRowState,
	type FlowSection,
	type Owner,
	type SampleStep,
} from '@codezo/order-detail';
import { Box } from 'lucide-react';
import { ProductImage, primaryProductImage } from '../../../components/ui/product-image';
import { formatDate } from '../../../lib/utils';
import type { ReactNode } from 'react';

type Order = NonNullable<GetClientOrderQuery['getClientOrder']>;
/** One payment the server is offering, already priced and worded. */
type PaymentOption = NonNullable<Order['paymentOptions']>[number];
type OrderItem = Order['items'][number];
type LifecycleStep = NonNullable<OrderItem['lifecycle']>[number];

const stepOf = (item: OrderItem, step: Step) => item.lifecycle?.find((s) => s.step === step);

/**
 * The lifecycle's own flags, turned into a row state. Mirrors the studio page's
 * `buildFlow.rowState` — see there for why `outstanding` exists.
 */
const rowState = (step: LifecycleStep | undefined): FlowRowState => {
	if (!step) return 'upcoming';
	if (step.skipped) return 'skipped';
	if (step.completed) return 'done';
	if (step.current) return 'active';
	if (step.outstanding) return 'outstanding';
	return 'upcoming';
};

/**
 * Done only when every step it covers is done; skipped only when all are; and
 * `outstanding` when the run finished with one of its steps still open.
 */
const mergedState = (steps: (LifecycleStep | undefined)[]): FlowRowState => {
	const present = steps.filter(Boolean) as LifecycleStep[];
	if (present.length === 0) return 'upcoming';
	if (present.every((s) => s.skipped)) return 'skipped';
	const real = present.filter((s) => !s.skipped);
	if (real.some((s) => s.current)) return 'active';
	if (real.every((s) => s.completed)) return 'done';
	if (real.some((s) => s.outstanding)) return 'outstanding';
	return 'upcoming';
};

/**
 * The four steps under "Production & delivery".
 *
 * Labels are OURS, not the server's: it calls the first one "Product payment",
 * which is right for a row standing on its own and redundant under a parent that
 * already names the product — the sample side's sub-step reads "Payment" too.
 * Dates and the outstanding sentence are the backend's.
 *
 * `paymentPanel` hangs off the step it acts on rather than under the whole run,
 * where it sat below "Delivered" and read as an action on the passage.
 */
const FULFILMENT: {
	key: Step;
	label: string;
	date: (i: OrderItem) => string | null | undefined;
}[] = [
	{ key: Step.ProductPayment, label: 'Payment', date: (i) => i.paidAt },
	{ key: Step.Production, label: 'Production', date: (i) => i.productionCompletedAt },
	{ key: Step.Delivery, label: 'Shipping', date: (i) => i.shippedAt },
	{ key: Step.Delivered, label: 'Delivered', date: (i) => i.actualDeliveryDate },
];

function fulfilmentSteps(item: OrderItem, paymentPanel?: ReactNode): SampleStep[] {
	return FULFILMENT.map(({ key, label, date }) => {
		const step = stepOf(item, key);
		const state = rowState(step);
		const at = date(item);
		return {
			key,
			label,
			state,
			at: state === 'done' && at ? formatDate(at) : undefined,
			// What is still owed on a step the item moved past, in the server's
			// words — "This product has not been paid for."
			note: step?.outstandingReason ?? undefined,
			// Payment is the step the client ACTS on; Shipping is the one they
			// follow. Both hang their content off the step it belongs to rather
			// than under the whole run, where it read as an action on the passage.
			//
			// Shipping's content is not a button: the boxes carrying this product
			// bring their own carrier, tracking and dates, and a product split
			// across two of them brings two sets. The step's date alone answered
			// for the first box only, which is what made a split product look like
			// it was travelling in one.
			panel:
				key === Step.ProductPayment ? (
					paymentPanel
				) : key === Step.Delivery ? (
					<ClientItemShipments item={item} />
				) : undefined,
		};
	});
}

/**
 * A payment step the client can still settle.
 *
 * `active` is standing on it; `outstanding` is a step the work overtook — the
 * item went into production with the invoice unpaid. Both are open, and both
 * have to offer the control, or the page names a debt it gives no way to clear.
 * The server is still the gate: with no matching `paymentOptions` entry the box
 * says what it is waiting for instead of showing a button.
 */
/**
 * How many boxes this product travels in, when that is worth saying.
 *
 * The boxes themselves are under the Shipping sub-step, inside a parent that is
 * collapsed until the order reaches it. Which left a product spread across two
 * boxes reading exactly like one travelling in a single box, all the way from
 * acceptance to dispatch — and for a client, "is my order coming in one delivery
 * or two" is a question they ask long before anything moves.
 *
 * So the collapsed parent says the count. A fact about the passage, readable
 * without opening anything.
 *
 * Only above one: "1 box" is the ordinary case and says nothing.
 */
const boxNote = (item: OrderItem): string | undefined => {
	const count = item.shipments?.length ?? 0;
	return count > 1 ? `${count} boxes` : undefined;
};

const payable = (state: FlowRowState) => state === 'active' || state === 'outstanding';

/**
 * The first thing a row still owes, in the server's words. Mirrors the studio
 * page's helper — a row covering several steps has to say WHICH of them was left
 * open, or a warn ring is a signal with no content.
 */
const outstandingNote = (
	state: FlowRowState,
	steps: (LifecycleStep | undefined)[]
): string | undefined => {
	// Only on a collapsed row: an `active` parent already has the sub-step
	// carrying this sentence open underneath it.
	if (state !== 'outstanding') return undefined;
	for (const step of steps) {
		if (step?.outstanding && step.outstandingReason) return step.outstandingReason;
	}
	return undefined;
};

const asOwner = (actor: SampleActionActor | null | undefined): Owner =>
	actor === SampleActionActor.Admin
		? 'admin'
		: actor === SampleActionActor.Client
			? 'client'
			: null;

/**
 * The order flow, from the client's side.
 *
 * The same spine the admin renders, off the same `OrderFlow` module and the same
 * lifecycle steps — what changes is `role="client"`, which flips every ownership
 * chip. An admin sees "Waiting on client" exactly where the client sees "Your
 * action", from one field, so the two surfaces cannot tell different stories
 * about whose move it is.
 *
 * ── Confirming items is not on this page at all ──
 *
 * The admin spine opens with a "Confirm items" row: the studio recording what
 * each item costs to make and which vetter is making it. None of that is the
 * client's business — not the figures, and not the step. So the row is absent
 * here rather than present-but-redacted: a greyed row labelled "Confirm items"
 * still tells a client there is an internal gate they are waiting behind, and
 * invites the question of what is in it.
 *
 * What they do see is the section's own chip — "Waiting on studio" — which is
 * true, sufficient, and says nothing about what the studio is doing.
 *
 * Paying is the reverse: the button lives here, and the admin's row says who it
 * is waiting for.
 */
export function OrderFlowSection({
	order,
	onChanged,
	onPayProduct,
	onPaySample,
	previewReason,
}: {
	order: Order;
	onChanged: () => void;
	/** Pay for this one product. The page owns the modal; this only asks. */
	onPayProduct: (item: OrderItem) => void;
	/** Pay for this one sample round, by id. */
	onPaySample: (sampleId: string) => void;
	/**
	 * Set while an admin is previewing the client view, and the sentence to say.
	 *
	 * The backend allowlists four mutations for a preview session and refuses the
	 * rest, so EVERY control here is disabled — paying and reviewing alike. A
	 * refused approve would otherwise fail on click with nothing explaining why.
	 */
	previewReason?: string | null;
}) {
	const items = order.items ?? [];

	/**
	 * This row's own payment, out of `paymentOptions`.
	 *
	 * `scope` says which id list a row carries — SAMPLE holds sample ids, PRODUCT
	 * holds order item ids — so a row is matched by asking whether its own id is in
	 * `targetIds`. Each option arrives priced, worded and blocked by the server.
	 *
	 * What this replaces: the order-level PAY_SAMPLES action, rendered under every
	 * product. It is one figure for ALL samples, so both products printed EUR
	 * 285.94 — the sum of the two — instead of 117.05 and 168.89. Anything
	 * order-wide belongs in the rail; the spine is per item.
	 *
	 * An EMPTY list is not "nothing to pay". The moment any invoice is raised every
	 * row disappears until it settles, so a missing option means a payment is in
	 * flight — never a reason to fall back to another figure, which is exactly what
	 * hid the wrong number.
	 */
	const paymentOptions = order.paymentOptions ?? [];
	const paymentInFlight = paymentOptions.length === 0;
	const optionFor = (scope: OrderPaymentOptionScope, id: string | null | undefined) =>
		id
			? (paymentOptions.find((o) => o.scope === scope && o.targetIds.includes(id)) ?? null)
			: null;

	/**
	 * Whether "pay for everything at once" is actually on offer.
	 *
	 * Shown on EVERY payable row. It was briefly shown on the first one only, to
	 * avoid repeating a sentence — but a line that appears under one product and
	 * not the next reads as a bug, and it is not repetition in any case: each row
	 * is its own decision, and the alternative is worth stating wherever that
	 * decision is being made.
	 *
	 * Only while the server offers an order-wide route that is not blocked.
	 * Pointing at a payment the client then cannot make is worse than silence.
	 *
	 * No amount here: order-wide figures belong in the rail. Printing one in a
	 * per-item row is what put EUR 285.94 under both products.
	 */
	const orderWideRoute = paymentOptions.find(
		(o) =>
			!o.blocked &&
			(o.scope === OrderPaymentOptionScope.FullOrder ||
				o.scope === OrderPaymentOptionScope.AllSamples)
	);

	/**
	 * There is NO setup section on this page, and `setupStage` is not read at all.
	 *
	 * Setup is the studio costing the order — what each item costs to make and who
	 * is making it. None of that is the client's business, so the row came out.
	 *
	 * It used to still GATE this spine: while the order was in SETUP every active
	 * row was forced back to `upcoming`, every panel suppressed and every product
	 * chip dropped, on the reasoning that "nothing has started yet". That is not
	 * true, and the page said so itself — the card would read "Your action" over a
	 * column of grey rows, because a client can be asked to pay for samples long
	 * before the studio has finished costing the order.
	 *
	 * The schema is explicit that `setupStage` is "orthogonal to the item
	 * lifecycle by design". Overriding `current` with it was the frontend
	 * second-guessing the server about which step an item is on.
	 */
	const productSections = items.map<FlowSection>((item) => {
		const delivered = rowState(stepOf(item, Step.Delivered)) === 'done';

		/**
		 * A date on the right, and ONLY on a finished row — the design's rule.
		 * Its right-hand slot is `done ? fcDate(…) : …`, so a running row says
		 * nothing there and what it has to say goes in the box below. Mono,
		 * because `fcDate` is.
		 */
		const at = (state: FlowRowState, verb: string, value?: string | null): ReactNode =>
			state === 'done' && value ? (
				// The verb belongs inside it - `opfbDoneMeta` is "approved Sep 21",
				// "paid Sep 23", "finished Oct 1", "delivered Oct 6", one mono span
				// each. A bare date leaves the reader working out which of the
				// row's dates it is.
				<span className="font-mono">{`${verb} ${formatDate(value)}`}</span>
			) : undefined;

		// ONE row over three steps — see the studio page for why. The round's own
		// stepper draws Payment, so a top-level "Sample payment" row above it said
		// the same thing twice; merged, the sub-steps are also visible while the
		// round is still being paid for.
		const samplePaymentState = rowState(stepOf(item, Step.SamplePayment));
		const sampleState = mergedState([
			stepOf(item, Step.SamplePayment),
			stepOf(item, Step.Sampling),
			stepOf(item, Step.Approval),
		]);
		const paymentState = rowState(stepOf(item, Step.ProductPayment));
		const deliveredState = rowState(stepOf(item, Step.Delivered));
		const fulfilmentState = mergedState([
			stepOf(item, Step.ProductPayment),
			stepOf(item, Step.Production),
			stepOf(item, Step.Delivery),
			stepOf(item, Step.Delivered),
		]);
		const approvedAt = item.samples?.find((s) => s.approvedAt)?.approvedAt;

		const rows: FlowRow[] = [
			{
				key: 'sample',
				label: 'Sample & approval',
				state: sampleState,
				// A date once the round is approved, and nothing before it.
				//
				// This used to carry the sample's fee and treatment — "fee DKK 700 ·
				// deducted from your order". It was the third place one sample was
				// priced, net, against a rail reading zero and a button charging 875
				// gross, and a client reading down the page met three numbers for it.
				// The money belongs where the money is: the summary card states the
				// charge and what happens to it. A flow row says where the work is.
				meta:
					sampleState === 'done'
						? at(sampleState, 'approved', approvedAt)
						: // What sampling never settled, once the order has moved past
							// it — otherwise the row is a warn ring over a label.
							outstandingNote(sampleState, [
								stepOf(item, Step.SamplePayment),
								stepOf(item, Step.Sampling),
								stepOf(item, Step.Approval),
							]),
				// While the round is live the row opens into its steps and, when it is
				// the client's turn, the approve/decline box.
				//
				// Once it is approved the same content stays reachable behind a link:
				// the photos, the virtual delivery, the studio's note and every earlier
				// round are the record of what was agreed before anything was made, and
				// dropping the panel with the row's `done` state put all of it out of
				// reach. Closed by default, so the spine still shows one open row.
				panel:
					// Open, or left open: a round whose payment the work overtook is
					// `outstanding`, not `active`, and folding it away behind the
					// approved-sample link would bury the invoice with it.
					sampleState === 'active' || sampleState === 'outstanding' ? (
						<>
							{/* While the PAYMENT step is still OPEN — standing on it, or
							    overtaken by a round that went into production unpaid.
							    This used to be `active` alone, on the reasoning that
							    offering an invoice beside a round already in production
							    would be a payment out of turn. Production no longer waits
							    for payment, so that is exactly backwards: the client was
							    told "This sample has not been paid for" and given no way
							    to pay it. */}
							{payable(samplePaymentState) && (
								<SamplePaymentRow
									option={optionFor(
										OrderPaymentOptionScope.Sample,
										liveSample(item)?.id
									)}
									inFlight={paymentInFlight}
									onPay={onPaySample}
									showCombined={!!orderWideRoute}
									previewReason={previewReason}
								/>
							)}
							<ClientSampleRounds
								item={item}
								onChanged={onChanged}
								previewReason={previewReason}
							/>
						</>
					) : sampleState === 'done' ? (
						<FlowDisclosure label="View the approved sample">
							<ClientSampleRounds
								item={item}
								onChanged={onChanged}
								previewReason={previewReason}
							/>
						</FlowDisclosure>
					) : undefined,
			},
			// ONE row over the four steps that get the goods made and shipped —
			// the same move Sample & approval made, for the same reason.
			//
			// Drawn flat they were four top-level rows for one passage, and the
			// client's copy read as four separate things to keep track of. Nested,
			// the spine says where the order has got to and the steps inside carry
			// the dates.
			//
			// The payment step is the one the client acts on, and it was the row
			// the spine was missing entirely: sampling would finish, the chip would
			// go quiet, and the flow sat on "Production" while the rail asked for
			// money. It is now a sub-step with the pay box under the run, and it
			// still disappears by itself on an order paid up front — the backend
			// marks PRODUCT_PAYMENT skipped, and a skipped step reads as
			// never-applied.
			{
				key: 'fulfilment',
				label: 'Production & delivery',
				state: fulfilmentState,
				meta:
					at(deliveredState, 'delivered', item.actualDeliveryDate) ??
					outstandingNote(fulfilmentState, [
						stepOf(item, Step.ProductPayment),
						stepOf(item, Step.Production),
						stepOf(item, Step.Delivery),
						stepOf(item, Step.Delivered),
					]) ??
					boxNote(item),
				panel: (
					<FlowSubSteps
						steps={fulfilmentSteps(
							item,
							payable(paymentState) ? (
								<ProductPaymentRow
									option={optionFor(OrderPaymentOptionScope.Product, item.id)}
									inFlight={paymentInFlight}
									onPay={() => onPayProduct(item)}
									showCombined={!!orderWideRoute}
									previewReason={previewReason}
								/>
							) : undefined
						)}
					/>
				),
			},
		];

		return {
			key: item.id,
			title: item.productName || 'Product',
			// The design's own line: quantity and what the line is worth. A node
			// rather than a string, because the figure goes through the app's
			// currency component rather than being formatted here.
			meta: (
				<>
					{`Qty ${item.quantity.toLocaleString()}`}
					{item.lineTotal != null && (
						<>
							{' · line total '}
							<CurrencyText amount={item.lineTotal} className="text-text-4" />
						</>
					)}
				</>
			),
			thumb: (
				<Thumb size={26}>
					<ProductImage
						image={primaryProductImage(item.productImages)}
						alt=""
						className="h-full w-full"
						fallback={<Box size={13} />}
					/>
				</Thumb>
			),
			// No chip while the order is still being set up. "Starts after setup"
			// names the same internal gate the confirm row was removed for, and
			// says it once per product directly under a section that has just said
			// it — "we are getting your order ready". The dimmed rows carry
			// not-started perfectly well on their own.
			badge: <OwnerChip owner={itemOwner(item)} role="client" done={delivered} />,
			// Each row decides its own panel above, rather than a blanket strip here.
			// The rule is unchanged for anything actionable — a payment panel appears
			// only on its active row, because offering a payment out of turn is how a
			// spine invites a move that is not available. What changed is that a
			// FINISHED sample keeps its record, folded away.
			rows,
		};
	});

	/* A card-level ownership chip lived here, for a gap that has since closed. The
	 * order-level payment routes belong to no single product, so before
	 * `waitingOn` a client owing a sample payment could find no chip anywhere and
	 * conclude the order was waiting on the studio. The step's own actor carries it
	 * now and the product sections say it themselves, leaving the card to repeat
	 * the first section one line above it. */

	return (
		<OrderFlow
			sections={productSections}
			// The client's wording, not ours. "Setup" is an internal word for the
			// studio costing the order, and it appeared three times on this card —
			// in the section, on every product, and here.
			meta="each product moves at its own pace"
		/>
	);
}

/* `sampleFee` lived here — the design's "fee DKK 310 · deducted from order" on
 * the Sample & approval row. Removed from the client's spine: it priced one
 * sample a third time, net, beside a summary line reading zero and a button
 * charging the same sample gross. The admin page keeps it; a studio reading the
 * spine is reading the deal, and the fee and its treatment are the deal. */

/**
 * Who owes this item's next move.
 *
 * One field. `waitingOn` is the actor of the item's current lifecycle step,
 * resolved server-side — null means nothing is pending, not unknown. The studio
 * page reads the same field, so the two surfaces cannot name different parties.
 *
 * What this replaces: the live sample round's actions, then the item's, with a
 * local rule for which entries counted as moves. Every version of that rule was
 * wrong in a different way, because "does this item have an available action" is
 * not the same question as "whose turn is it".
 *
 * One case worth knowing: under admin-manual payment terms the client is never
 * asked to pay, and `waitingOn` says "studio" at the payment steps accordingly.
 * That is the server reading the order's payment modes — not something this page
 * could have worked out from an action list.
 */
function itemOwner(item: OrderItem): Owner {
	return asOwner(item.waitingOn);
}

/**
 * A payment row's own strip — the option the server priced for THIS item.
 *
 * Everything shown is the option's: its label, its amount, its blocked reason.
 * Nothing here decides whether a payment is possible or what it costs.
 *
 * ── Why splitting the payment is safe now ──
 *
 * An earlier note here argued the opposite: that paying one product could
 * absorb part of a paid sample credit and strand the rest behind a credit note
 * that does not exist. That was true of a per-item button we priced ourselves.
 * It is not true of these rows — a PRODUCT option is blocked until its own
 * prepaid sample is settled, and the backend prices the row with the same credit
 * rule `paymentSummary` uses, so the four itemised rows sum to the order total
 * rather than over-quoting it.
 *
 * That reconciliation is the test worth keeping: if the itemised rows do not sum
 * to `totalOutstandingAmount`, one side is wrong.
 */
function PaymentOptionBox({
	option,
	inFlight,
	waiting,
	action,
	showCombined,
	previewReason,
}: {
	option: PaymentOption | null;
	/** An invoice is open, so the server is offering nothing at all right now. */
	inFlight: boolean;
	/** Offer the combined route as an alternative under this row. */
	showCombined?: boolean;
	/** What is true when there is no option and nothing is in flight. */
	waiting: ReactNode;
	/** The control, when the row has one and the option is usable. */
	action?: ReactNode;
	/** Set while an admin previews the client view — the control is refused. */
	previewReason?: string | null;
}) {
	// An empty `paymentOptions` means a payment is being processed — every row
	// loses its option at once. Saying so is the whole point: this is the state
	// most likely to be read as a broken panel.
	if (inFlight) {
		return (
			<ActionBox>
				<ActionContext>
					A payment is being processed. The rest becomes payable again once it settles.
				</ActionContext>
			</ActionBox>
		);
	}

	if (!option) {
		return (
			<ActionBox>
				<ActionContext>{waiting}</ActionContext>
			</ActionBox>
		);
	}

	if (option.blocked) {
		return (
			<ActionBox>
				<ActionContext>{option.blockedReason ?? 'Not payable yet'}</ActionContext>
			</ActionBox>
		);
	}

	return (
		<>
			<ActionBox mine>
				<ActionContext>
					{option.label} ·{' '}
					<CurrencyText amount={option.amount} currency={option.currency} /> incl. VAT
				</ActionContext>
				{previewReason ? (
					<span className="text-2xs text-status-warn">{previewReason}</span>
				) : (
					action
				)}
			</ActionBox>
			{/* Under the box, not inside it. The box is about THIS payment, and a
			    sentence about a different one sitting beside its button is how a row
			    stops reading as a single decision. */}
			{showCombined && (
				<div className="mt-2 ml-0.5 text-xs leading-snug text-text-3">
					Or settle it in one go from{' '}
					<span className="font-medium text-text-1">Order total</span> — one invoice, and
					nothing later waits on a payment clearing.
				</div>
			)}
		</>
	);
}

/**
 * The sample payment row.
 *
 * Charges THIS round alone. The option's `targetIds` are the sample ids the
 * server priced, and they are handed straight to the checkout — so the figure
 * beside the button is what leaves the account. Paying for every sample at once
 * is still the rail's route, where the label says so.
 */
function SamplePaymentRow({
	option,
	inFlight,
	onPay,
	showCombined,
	previewReason,
}: {
	option: PaymentOption | null;
	inFlight: boolean;
	onPay: (sampleId: string) => void;
	showCombined?: boolean;
	previewReason?: string | null;
}) {
	const sampleId = option?.targetIds[0];
	return (
		<PaymentOptionBox
			option={option}
			inFlight={inFlight}
			showCombined={showCombined}
			previewReason={previewReason}
			waiting="Waiting on the studio to raise the sample invoice."
			action={
				sampleId ? (
					<Button size="sm" onClick={() => onPay(sampleId)}>
						Pay for this sample
					</Button>
				) : undefined
			}
		/>
	);
}

/**
 * The product payment row.
 *
 * `createOrderPayment` takes item ids and the modal already sends them, so this
 * one charges exactly the product the row sits under — the figure beside the
 * button is what leaves the account.
 */
function ProductPaymentRow({
	option,
	inFlight,
	onPay,
	showCombined,
	previewReason,
}: {
	option: PaymentOption | null;
	inFlight: boolean;
	onPay: () => void;
	showCombined?: boolean;
	previewReason?: string | null;
}) {
	return (
		<PaymentOptionBox
			option={option}
			inFlight={inFlight}
			showCombined={showCombined}
			previewReason={previewReason}
			waiting="Ready to invoice — waiting on the studio."
			action={
				<Button size="sm" onClick={onPay}>
					Pay for this product
				</Button>
			}
		/>
	);
}
